import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import FormDialog from 'components/FormDialog';
import DataTable from 'components/Table/DataTable';
import { getFormsByClientByStatus } from 'graphql/queries';
import FormEditButton from './FormEditButton';
import cache from 'utilities/cache';
import { download } from 'utilities/file';
import { Storage } from 'aws-amplify';
import { toastr } from 'react-redux-toastr';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import { useCache } from 'CacheProvider';
import { asyncListAll } from 'utilities/graph';
import { PDFDocument } from 'pdf-lib';
import fontkit from 'pdf-fontkit'; // https://github.com/Hopding/pdf-lib/issues/494#issuecomment-695818400
import PdfViewer from './PdfViewer';
import { getEldersByClientByStatus, getElderDeliveryGroupsByClientByStatus } from './queries';
import { getDeliveryGroupsByClientByIsActive } from 'graphql/queries';
import { getFormFieldsValueMapping } from './utils';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import LinearProgress from '@material-ui/core/LinearProgress';
import { sortBy } from 'utilities/sorting';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemText from '@material-ui/core/ListItemText';

export default function FormTable({
  title = '表單',
  description,
  ...props
}) {
  const [indexes, setIndexes] = useState();
  const [queryParams, setQueryParams] = useState();
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedFileS3Key, setSelectedFileS3Key] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { isAdmin } = useCache();
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState('');
  const [elders, setElders] = useState([]);
  const [deliveryGroups, setDeliveryGroups] = useState([]);
  const [elderDeliveryGroups, setElderDeliveryGroups] = useState([]);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);
  const [selectedFileForGroup, setSelectedFileForGroup] = useState(null);

  const clientName = cache.get('app:name');
  const clientId = cache.get('app:facilityId');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const fileDownload = async (fileS3Key, name) => {
    try {
      const url = await Storage.get(fileS3Key);
      const fileResponse = await fetch(url);
      if (!fileResponse.ok) {
        throw new Error('下載檔案失敗');
      }
      const blob = await fileResponse.blob();
      download(`${name}.pdf`, blob);
    } catch (e) {
      console.error('檔案下載失敗:', e.message);
      toastr.error(`檔案下載失敗: ${e.message}`);
    }
  };

  const getFilteredElders = (isDisadvantaged) => {
    const filteredElders = elders.filter((elder) => Boolean(elder.isDisadvantaged) === Boolean(isDisadvantaged));
    return filteredElders.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  };

  const getGroupElders = (deliveryGroupId) => {
    const groupElderIds = elderDeliveryGroups
      .filter((edg) => edg.deliveryGroupId === deliveryGroupId)
      .sort(sortBy('sortOrder'))
      .map((edg) => edg.elderId);

    const groupElders = [];
    groupElderIds.forEach((elderId) => {
      const elder = elders.find(({ id }) => id === elderId);
      if (elder) {
        groupElders.push(elder);
      }
    });

    return groupElders;
  };

  const generatePdfWithElders = async (fileS3Key, name, elders, progressPrefix = '') => {
    if (elders.length === 0) {
      toastr.error('沒有長輩資料，無法生成表單');
      return;
    }

    setIsLoading(true);
    setDownloadProgress(0);
    setDownloadStatus('準備中...');
    let fontBytes;

    try {
      setDownloadStatus('載入長輩資料與字體...');
      const response = await fetch('/fonts/NotoSansTC-Regular.ttf');
      if (!response.ok) {
        throw new Error(`Failed to fetch font: ${response.status} ${response.statusText}`);
      }
      fontBytes = await response.arrayBuffer();
      setDownloadProgress(10);
    } catch (e) {
      console.error('字體載入失敗:', e);
      toastr.error('字體載入失敗，請檢查網路連線後再重新嘗試');
      setIsLoading(false);
      return;
    }

    try {
      setDownloadStatus('載入PDF模板...');
      const fileUrl = await Storage.get(fileS3Key);
      const existingPdfBytes = await fetch(fileUrl).then((res) => res.arrayBuffer());
      const mergedPdfDoc = await PDFDocument.create();
      setDownloadProgress(20);

      const totalElders = elders.length;

      setDownloadStatus('生成PDF檔案中...');
      for (let i = 0; i < elders.length; i++) {
        const elder = elders[i];
        const pdfDoc = await PDFDocument.load(existingPdfBytes);
        const form = pdfDoc.getForm();
        pdfDoc.registerFontkit(fontkit);
        const font = await pdfDoc.embedFont(fontBytes, { subset: true });

        const rawUpdateFieldAppearances = form.updateFieldAppearances.bind(form);
        form.updateFieldAppearances = function() {
          return rawUpdateFieldAppearances(font);
        };

        const formFieldsValueMapping = getFormFieldsValueMapping(elder);
        formFieldsValueMapping.forEach(({ fieldName, value }) => {
          try {
            const field = form.getTextField(fieldName);
            if (field) {
              field.setText(value || '');
            }
          } catch (e) {
            // console.log(e);
          }
        });

        const pdfBytes = await pdfDoc.save();
        const filledPdfDoc = await PDFDocument.load(pdfBytes);
        const copiedPages = await mergedPdfDoc.copyPages(filledPdfDoc, filledPdfDoc.getPageIndices());
        copiedPages.forEach((page) => mergedPdfDoc.addPage(page));

        // 更新進度
        const progress = 20 + Math.floor((i + 1) / totalElders * 70);
        setDownloadProgress(progress);
        setDownloadStatus(`${progressPrefix}處理第 ${i + 1}/${totalElders} 位長輩資料...`);
      }

      setDownloadStatus('完成PDF生成，準備下載...');
      const pdfBytes2 = await mergedPdfDoc.save();
      setDownloadProgress(95);

      const blob = new Blob([pdfBytes2], { type: 'application/pdf' });
      download(`${name}.pdf`, blob);

      setDownloadProgress(100);
      setDownloadStatus('下載完成！');
      setTimeout(() => {
        setIsLoading(false);
        setDownloadProgress(0);
        setDownloadStatus('');
      }, 1000);
    } catch (e) {
      console.error('PDF生成失敗:', e.message);
      toastr.error(`PDF生成失敗: ${e.message}`);
      setIsLoading(false);
      setDownloadProgress(0);
      setDownloadStatus('');
    }
  };

  const fileDownloadWithElders = async (fileS3Key, name, isDisadvantaged=true) => {
    const type = isDisadvantaged ? '弱勢長輩' : '自費戶';
    const filteredElders = getFilteredElders(isDisadvantaged);

    if (filteredElders.length === 0) {
      toastr.error(`沒有${type}資料，無法生成表單`);
      return;
    }

    await generatePdfWithElders(fileS3Key, name, filteredElders, type);
  };

  const fileDownloadWithDeliveryGroup = async (fileS3Key, name, deliveryGroupId) => {
    const groupElders = getGroupElders(deliveryGroupId);

    if (groupElders.length === 0) {
      toastr.error('該送餐群組沒有送餐對象，無法生成表單');
      return;
    }

    const deliveryGroup = deliveryGroups.find(({ id }) => id === deliveryGroupId);
    const groupName = deliveryGroup ? deliveryGroup.name : '未知群組';

    await generatePdfWithElders(fileS3Key, name, groupElders, `${groupName}`);
  };

  const openFilePreview = (fileS3Key, name) => {
    setSelectedFileName(name);
    setSelectedFileS3Key(fileS3Key);
    setOpenPreview(true);
  };

  const handleGroupMenuOpen = (event, fileS3Key, name) => {
    event.preventDefault();
    setGroupMenuAnchor(event.currentTarget);
    setSelectedFileForGroup({ fileS3Key, name });
  };

  const handleGroupMenuClose = () => {
    setGroupMenuAnchor(null);
    setSelectedFileForGroup(null);
  };

  const handleGroupSelect = (deliveryGroupId) => {
    if (selectedFileForGroup) {
      const deliveryGroup = deliveryGroups.find(({ id }) => id === deliveryGroupId);
      if (deliveryGroup) {
        fileDownloadWithDeliveryGroup(selectedFileForGroup.fileS3Key, `${selectedFileForGroup.name}_${deliveryGroup.name}`, deliveryGroupId);
      } else {
        toastr.error('找不到選定的送餐群組');
      }
    }
    handleGroupMenuClose();
  };

  const columns = [
    {
      name: 'id',
      label: '編號',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'name',
      label: '表單名稱',
      options: {
        display: true,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'fileS3Key',
      label: 'fileS3Key',
      options: {
        display: 'excluded',
        filter: false,
        sort: false,
      },
    },
    {
      name: 'category',
      label: '類別',
      options: {
        display: false,
        filter: true,
        sort: true,
      },
    },
    {
      name: 'subcategory',
      label: '子類別',
      options: {
        display: false,
        filter: true,
        sort: true,
      },
    },
    {
      name: 'status',
      label: '狀態',
      options: {
        display: false,
        filter: true,
        sort: true,
      },
      hideInOrgMode: true,
    },
    {
      name: 'note',
      label: '備註',
      options: {
        display: true,
        filter: false,
        sort: false,
      },
    },
    {
      name: 'createdBy',
      label: '創立者',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'updatedBy',
      label: '更新者',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'createdAt',
      label: '創立於',
      type: 'datetime',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'updatedAt',
      label: '更新於',
      type: 'datetime',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'fileS3Key',
      label: ' ',
      options: {
        display: 'always',
        filter: false,
        sort: false,
        download: false,
        print: false,
        customBodyRender(fileS3Key, tableMeta) {
          const name = tableMeta.rowData[1];
          return (
            <Box display="flex" flexDirection="row" alignItems="top">
              <Tooltip
                title={'下載空白表單'}
                placement="bottom"
              >
                <Box display="flex" flexDirection="column" alignItems="center" p={1}>
                  <IconButton
                    onClick={async () => {
                      await fileDownload(fileS3Key, name);
                    }}
                    disabled={isLoading}
                  >
                    {<CloudDownloadIcon />}
                  </IconButton>
                  <Typography variant='caption'>空白表單</Typography>
                </Box>
              </Tooltip>
              <Tooltip
                title={'下載預填弱勢長輩資料的表單'}
                placement="bottom"
              >
                <Box display="flex" flexDirection="column" alignItems="center" p={1}>
                  <IconButton
                    onClick={async () => {
                      await fileDownloadWithElders(fileS3Key, `${name}_弱勢長輩`);
                    }}
                    disabled={isLoading || elders.filter((elder) => elder.isDisadvantaged).length === 0}
                  >
                    {<CloudDownloadIcon />}
                  </IconButton>
                  <Typography variant='caption'>弱勢長輩</Typography>
                </Box>
              </Tooltip>
              <Tooltip
                title={'下載預填自費戶資料的表單'}
                placement="bottom"
              >
                <Box display="flex" flexDirection="column" alignItems="center" p={1}>
                  <IconButton
                    onClick={async () => {
                      await fileDownloadWithElders(fileS3Key, `${name}_自費戶`, false);
                    }}
                    disabled={isLoading || elders.filter((elder) => !elder.isDisadvantaged).length === 0}
                  >
                    {<CloudDownloadIcon />}
                  </IconButton>
                  <Typography variant='caption'>自費戶</Typography>
                </Box>
              </Tooltip>
              <Tooltip
                title={'下載預填群組資料的表單'}
                placement="bottom"
              >
                <Box display="flex" flexDirection="column" alignItems="center" p={1}>
                  <IconButton
                    onClick={(event) => {
                      handleGroupMenuOpen(event, fileS3Key, name);
                    }}
                    disabled={isLoading || deliveryGroups.length === 0}
                  >
                    {<CloudDownloadIcon />}
                  </IconButton>
                  <Typography variant='caption'>送餐群組</Typography>
                </Box>
              </Tooltip>
              <Tooltip
                title={'檢視表單'}
                placement="bottom"
              >
                <Box display="flex" flexDirection="column" alignItems="center" p={1}>
                  <IconButton
                    onClick={() => {
                      openFilePreview(fileS3Key, name);
                    }}
                    disabled={isLoading}
                  >
                    <VisibilityIcon />
                  </IconButton>
                  <Typography variant='caption'>檢視</Typography>
                </Box>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  useEffect(() => {
    const getDeliveryGroupData = async () => {
      try {
        const [
          elders,
          deliveryGroups,
          elderDeliveryGroups,
        ] = await Promise.all([
          asyncListAll(getEldersByClientByStatus, { clientId, status: { eq: '使用中' }, limit: 1000 }),
          asyncListAll(getDeliveryGroupsByClientByIsActive, { clientId, limit: 1000 }),
          asyncListAll(getElderDeliveryGroupsByClientByStatus, { clientId, status: { eq: '使用中' }, limit: 1000 }),
        ]);
        setElders(elders);
        setDeliveryGroups(deliveryGroups.sort(sortBy('name')));
        setElderDeliveryGroups(elderDeliveryGroups);
      } catch (error) {
        console.error('載入送餐群組資料失敗:', error);
        toastr.error('載入送餐群組資料失敗，請重新整理頁面');
      }
    };

    getDeliveryGroupData();

    setIndexes([
      {
        name: 'getFormsByClientByStatus',
        label: '按狀態查詢',
        partitionKey: 'clientId',
        sortKey: 'status',
        fields: [{
          label: '機構',
          key: 'clientId',
          type: 'select',
          options: [{ value: clientId, label: clientName }],
        }, {
          label: '狀態',
          key: 'status',
          type: 'select',
          options: ['使用中', '已停用'].map((item) => {
            return { value: item, label: item };
          }),
        }],
        operation: getFormsByClientByStatus,
      },
    ]);
    setQueryParams({
      clientId,
      status: '使用中',
    });
  }, [clientId, clientName]);


  if (!indexes || !queryParams) return null;

  return (
    <React.Fragment>
      <DataTable
        indexes={indexes}
        queryDefaultParams={queryParams}
        title={title}
        description={description}
        columns={columns}
        editButton={isAdmin ? FormEditButton : undefined}
        {...props}
      />
      {openPreview &&
        <FormDialog
          title={selectedFileName}
          openOnInit={true}
          onClose={() => {
            setSelectedFileName('');
            setSelectedFileS3Key('');
            setOpenPreview(false);
          }}
          fullScreen={fullScreen}
        >
          <PdfViewer fileS3Key={selectedFileS3Key} />
        </FormDialog>}
      <Dialog
        open={isLoading}
        onClose={() => {}}
        PaperProps={{
          style: {
            width: '400px',
            maxWidth: '90vw',
          },
        }}
      >
        <DialogTitle>PDF 生成中...</DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress variant="determinate" value={downloadProgress} />
          </Box>
          <Typography variant="body2" color="textSecondary">
            {downloadStatus}
          </Typography>
        </DialogContent>
      </Dialog>
      <Menu
        anchorEl={groupMenuAnchor}
        open={Boolean(groupMenuAnchor)}
        onClose={handleGroupMenuClose}
        keepMounted
      >
        {deliveryGroups.length > 0 ? (
          deliveryGroups.map((deliveryGroup) => (
            <MenuItem key={deliveryGroup.id} onClick={(event) => {
              event.preventDefault();
              handleGroupSelect(deliveryGroup.id);
            }}>
              <ListItemText primary={deliveryGroup.name} />
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <ListItemText primary="沒有可用的送餐群組" />
          </MenuItem>
        )}
      </Menu>
    </React.Fragment>);
}

FormTable.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
};
